import { useTranslation } from '@silospot/i18n/react';
import { LOCALES, LOCALE_LABELS, type Locale } from '@silospot/shared';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Btn, Card, CardHead, PageHeader, Segmented } from '../../components/cld';
import { useChangeLocale } from '../../lib/locale';
import { useAuthStore } from '../../stores/auth-store';
import { useLocaleStore } from '../../stores/locale-store';

function Field({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#f1f3f6] py-3 last:border-0">
      <span className="text-[13px] text-text-2">{label}</span>
      <span className="text-[13.5px] font-medium text-text">{children}</span>
    </div>
  );
}

export function ProfilePage(): ReactElement {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const locale = useLocaleStore((s) => s.locale);
  const changeLocale = useChangeLocale();
  const [lang, setLang] = useState<Locale>(locale);

  return (
    <div>
      <PageHeader title={t('profile.title')} sub={user?.email} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHead title={t('profile.account')} />
          <Field label={t('profile.name')}>{user?.name ?? '—'}</Field>
          <Field label={t('profile.email')}>{user?.email ?? '—'}</Field>
          <Field label={t('profile.language')}>
            <Segmented
              size="sm"
              value={lang}
              onChange={(v) => {
                setLang(v);
                changeLocale(v);
              }}
              options={LOCALES.map((l: Locale) => ({ value: l, label: l.toUpperCase() }))}
            />
          </Field>
          <p className="mt-2 text-[11.5px] text-text-3">{LOCALE_LABELS[lang]}</p>
        </Card>

        <Card>
          <CardHead title={t('profile.security')} />
          <Field label={t('profile.password')}>
            <Btn onClick={() => navigate('/forgot-password')}>{t('profile.changePassword')}</Btn>
          </Field>
          <Field label={t('profile.twofa')}>
            <Badge tone={user?.totpEnabled ? 'clean' : 'neutral'} dot>
              {user?.totpEnabled ? t('profile.enabled') : t('profile.disabled')}
            </Badge>
          </Field>
        </Card>
      </div>
    </div>
  );
}
